<template>
  <v-sheet color="surface" rounded="lg" class="pa-4 email-editor">
    <v-container :fluid="true">
      <!-- Success state -->
      <v-container v-if="emailSent" class="pa-0">
        <v-row>
          <v-col class="text-center py-8">
            <v-icon color="success" size="64" class="mb-4">mdi-check-circle</v-icon>
            <div class="text-h5 text-primary mb-2">Email Sent Successfully</div>
            <div
              class="text-body-1 text-medium-emphasis mb-6"
              style="max-width: 500px; margin: 0 auto; word-break: break-word">
              Your email has been sent to {{ sentToSummary }}
            </div>
            <v-btn-lh @click="onSendAnother">Send Another Email</v-btn-lh>
          </v-col>
        </v-row>
      </v-container>

      <!-- Email form -->
      <v-container v-else class="pa-0">
        <v-row>
          <v-col>
            <div v-if="props.title" class="email-editor__title">{{ props.title }}</div>
            <div v-if="errorMsg" class="text-error">{{ errorMsg }}</div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-combobox
              ref="emailCombobox"
              v-model="toEmails"
              v-model:search="emailSearchText"
              label="To"
              variant="outlined"
              bg-color="white"
              multiple
              chips
              closable-chips
              :items="filteredEmailSuggestions"
              :loading="emailAutocompleteLoading"
              :rules="[emailListRule]"
              :hide-no-data="!emailSearchText"
              @update:search="emailAutocomplete.searchUpdate"
              @keydown.enter.prevent="onEmailEnter"
              @keydown.tab.prevent="onEmailEnter"
              @keydown.space.prevent="onEmailEnter">
              <template #chip="{ props: chipProps, item }">
                <v-chip
                  v-bind="chipProps"
                  :color="isEmailValid(item.value) ? 'primary' : 'error'"
                  variant="outlined"
                  rounded="lg"
                  closable>
                  {{ item.value }}
                </v-chip>
              </template>
            </v-combobox>
          </v-col>
        </v-row>
        <v-row v-if="emailTemplateItems.length > 0">
          <v-col>
            <v-select
              variant="outlined"
              bg-color="white"
              :items="emailTemplateItems"
              item-title="title"
              item-value="value"
              label="Select an email template"
              :model-value="selectedEmailTemplate"
              clearable
              return-object
              @update:model-value="onEmailTemplateSelected" />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="subject"
              label="Subject"
              variant="outlined"
              bg-color="white"
              :rules="[requiredRule]" />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="templateItems.length > 0">
            <v-combobox
              variant="outlined"
              bg-color="white"
              :items="templateItems"
              label="Select a content template"
              :model-value="selectedTemplate"
              @update:model-value="onTemplateSelected" />
          </v-col>
        </v-row>
        <v-row v-if="props.attachmentOptions.length > 0">
          <v-col>
            <v-combobox
              variant="outlined"
              bg-color="white"
              :items="props.attachmentOptions"
              label="Select an attachment"
              :model-value="selectedAttachment"
              clearable
              @update:model-value="onAttachmentSelected" />
            <v-checkbox
              v-if="supportingDocumentsUrls.length > 0"
              v-model="includeSupportingDocuments"
              label="Include supporting documents"
              hide-details
              class="mt-2" />
            <div
              v-if="includeSupportingDocuments && supportingDocumentsUrls.length > 0 && selectedAttachment"
              class="text-caption text-medium-emphasis mt-1">
              {{ getAttachmentSummary() }}
            </div>
          </v-col>
        </v-row>
        <v-row v-if="availableSuggestedFiles.length > 0">
          <v-col>
            <div class="text-caption text-medium-emphasis mb-1">Available to attach:</div>
            <div class="d-flex flex-wrap ga-2">
              <v-chip
                v-for="file in availableSuggestedFiles"
                :key="file.link"
                color="primary"
                variant="tonal"
                rounded="lg"
                @click="onAttachSuggestedFile(file)">
                <v-icon start size="small">mdi-plus</v-icon>
                {{ file.name }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
        <v-row v-if="uploadedFiles.length > 0">
          <v-col>
            <div class="d-flex flex-wrap ga-2">
              <v-chip
                v-for="file in uploadedFiles"
                :key="file.id"
                color="primary"
                variant="outlined"
                rounded="lg"
                closable
                @click:close="removeUploadedFile(file.id)">
                <v-icon start size="small">mdi-paperclip</v-icon>
                {{ file.name }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea v-model="body" label="Email Body" />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="props.cancelButton" cols="4">
            <v-btn-lh :loading="loading" @click="onCancel">Cancel</v-btn-lh>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="auto" align="right">
            <v-btn-lh :loading="uploading" :disabled="loading" @click="onAttachFile">Attach a File</v-btn-lh>
            <input
              ref="fileInputRef"
              type="file"
              accept=".tif, .tiff, .pdf, .png, .jpg, .jpeg"
              class="d-none"
              @change="handleFileUpload" />
          </v-col>
          <v-col cols="auto" align="right">
            <v-btn-lh :disabled="!isFormValid" :loading="loading" @click="onSend">
              {{ props.sendButtonText }}
            </v-btn-lh>
          </v-col>
        </v-row>
      </v-container>
    </v-container>
  </v-sheet>
</template>

<script setup>
import { onMounted, ref, watch, computed, nextTick } from "vue";
import { templateService } from "@/services/templateService";
import { emailService } from "@/services/emailService";
import { mainServices } from "@/services/mainServices";
import { IntakeOrder, IntakeDocument } from "@/models/intake";
import { ClientTemplate } from "@/models/templates";
import { requiredRule } from "@/utils/form";
import { isEmailValid } from "@/utils/validators";
import { uploadService, validateFile } from "@/services/uploadService";
import { uuid } from "vue-uuid";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { useRemoteAutocompleteSearch } from "@/utils/autocomplete";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  types: {
    type: Array,
    required: false,
    default: () => [],
  },
  toEmail: {
    type: String,
    required: false,
    default: null,
  },
  toEmails: {
    type: Array,
    required: false,
    default: () => [],
  },
  defaultSubject: {
    type: String,
    required: false,
    default: "",
  },
  intakeOrder: {
    type: IntakeOrder,
    required: false,
    default: null,
  },
  intakeDocument: {
    type: IntakeDocument,
    required: false,
    default: null,
  },
  orderInfo: {
    type: Object,
    required: false,
    default: null,
  },
  cancelButton: {
    type: Boolean,
    required: false,
    default: true,
  },
  sendButtonText: {
    type: String,
    required: false,
    default: "Send Email",
  },
  deferSend: {
    type: Boolean,
    required: false,
    default: false,
  },
  attachmentOptions: {
    type: Array,
    required: false,
    default: () => [],
  },
  preselectedAttachment: {
    type: Object,
    required: false,
    default: null,
  },
  prefilledContent: {
    type: String,
    required: false,
    default: null,
  },
  frontendTemplateName: {
    type: String,
    required: false,
    default: null,
  },
  frontendTemplate: {
    type: Object,
    required: false,
    default: null,
  },
  supportingDocuments: {
    type: Array,
    required: false,
    default: () => [],
  },
  templateContext: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  /** @type {Array<{ name: string, link: string }>} Files the user can opt-in to attach via a single click. */
  suggestedFiles: {
    type: Array,
    required: false,
    default: () => [],
  },
});
const emit = defineEmits(["onComplete", "onPreSend", "onFrontendTemplateSelected"]);

const requirementsStore = useRequirementsStore();
const clientCompanyId = computed(() => requirementsStore.clientConfig?.id);

const templates = ref([]);
const templateItems = ref([]);
const emailTemplates = ref([]); // Client company email templates from backend
const emailTemplateItems = ref([]);
const selectedEmailTemplate = ref(null);
const uploading = ref(false);
const selectedTemplate = ref(null);
const body = ref("");
const subject = ref(props.defaultSubject || "");
const errorMsg = ref("");
const loading = ref(false);
const toEmails = ref([]);
const downloadLinks = ref([]);
const emailSent = ref(false);
const sentToSummary = ref("");

const emailSearchText = ref("");
const emailCombobox = ref(null);

const emailAutocomplete = useRemoteAutocompleteSearch(
  async (search) => {
    if (!clientCompanyId.value) return [];
    const res = await mainServices.getClientCompanyData(clientCompanyId.value, "email_addressbook", search);
    const items = (res.results || res || []).map((r) => r.textValue);
    return items;
  },
  { allowFreeText: true, allowEmptySearch: true }
);

const filteredEmailSuggestions = computed(() =>
  (emailAutocomplete.itemsRef.value || []).filter((item) => !toEmails.value.includes(item))
);
const emailAutocompleteLoading = computed(() => emailAutocomplete.loadingRef.value);

/**
 * Substitute template variables with values from context.
 * Supports variables like {{patient.firstName}}, {{orderForm.providerFirstName}}, etc.
 */
const substituteTemplateVariables = (template, context) => {
  if (!template) return "";
  return template.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
    const keys = path.trim().split(".");
    let value = context;
    for (const key of keys) {
      if (value && typeof value === "object" && key in value) {
        value = value[key];
      } else {
        return ""; // Return empty string if path not found
      }
    }
    return value ?? "";
  });
};

/**
 * Apply an email template to the form, substituting variables with context values
 */
const applyEmailTemplate = (template) => {
  if (!template) return;
  const context = props.templateContext || {};
  // Handle both camelCase (from humps) and snake_case (raw) property names
  const subjectTpl = template.subjectTemplate || template.subject_template || "";
  const bodyTpl = template.bodyTemplate || template.body_template || "";
  subject.value = substituteTemplateVariables(subjectTpl, context);
  body.value = substituteTemplateVariables(bodyTpl, context);
};

/**
 * Handle email template selection
 */
const onEmailTemplateSelected = (selected) => {
  selectedEmailTemplate.value = selected;
  if (!selected) {
    // Clear template-related fields if template is cleared
    return;
  }
  // v-select returns the item object when using items with title/value
  // The value property contains the actual ID
  const templateId = typeof selected === "object" ? selected.value : selected;
  const template = emailTemplates.value.find((t) => t.id === templateId);
  if (template) {
    applyEmailTemplate(template);
  }
};

/**
 * Fetch email templates for the client company
 */
const fetchEmailTemplates = async () => {
  if (!clientCompanyId.value) return;
  try {
    const response = await mainServices.getEmailTemplates(clientCompanyId.value);
    // Handle both paginated and non-paginated responses
    const templatesData = response.results || response || [];
    emailTemplates.value = Array.isArray(templatesData) ? templatesData : [];
    emailTemplateItems.value = emailTemplates.value.map((t) => ({
      title: t.name,
      value: t.id,
    }));
  } catch {
    // Silently fail - templates dropdown will simply not appear
  }
};

// Validation rule for the email list
const emailListRule = (emails) => {
  if (!emails || emails.length === 0) return "At least one email address is required";
  const invalidEmails = emails.filter((e) => !isEmailValid(e));
  if (invalidEmails.length > 0) return `Invalid email: ${invalidEmails[0]}`;
  return true;
};

// Handle Enter/Tab/Space to add email
const onEmailEnter = async () => {
  const email = emailSearchText.value?.trim();
  if (email && !toEmails.value.includes(email)) {
    toEmails.value = [...toEmails.value, email];
  }
  // Force clear by blurring and refocusing - this resets Vuetify's internal state
  await nextTick();
  if (emailCombobox.value) {
    const inputEl = emailCombobox.value.$el?.querySelector("input");
    if (inputEl) {
      inputEl.blur();
      await nextTick();
      inputEl.value = "";
      emailSearchText.value = "";
      inputEl.focus();
    }
  }
};

const selectedAttachment = ref(props.preselectedAttachment || null);
const includeSupportingDocuments = ref(false);

const supportingDocumentsUrls = computed(() => {
  return props.supportingDocuments || [];
});

const isFormValid = computed(() => {
  const hasValidEmails = toEmails.value.length > 0 && toEmails.value.every((e) => e && isEmailValid(e));
  const hasBody = body.value && body.value.trim().length > 0;
  const hasSubject = subject.value && subject.value.trim().length > 0;
  return hasValidEmails && hasBody && hasSubject;
});

// Initialize toEmails from props
const initializeEmails = () => {
  const emails = [];
  if (props.toEmails && props.toEmails.length > 0) {
    emails.push(...props.toEmails.filter((e) => e).map((e) => e.trim()));
  } else if (props.toEmail) {
    emails.push(props.toEmail.trim());
  }
  toEmails.value = emails;
};

onMounted(() => {
  initializeEmails();
  // Fetch backend templates if types are provided
  if (props.types && props.types.length > 0) {
    fetchTemplates();
  }
  // Fetch email templates if client company ID is available
  if (clientCompanyId.value) {
    fetchEmailTemplates();
  }
  // Prefetch address book so suggestions appear on focus
  emailAutocomplete.searchUpdate("");
});

watch(
  () => props.types,
  () => {
    if (props.types && props.types.length > 0 && !props.prefilledContent) {
      fetchTemplates();
    }
  },
  { immediate: false }
);

watch(
  clientCompanyId,
  (newId) => {
    if (newId) {
      fetchEmailTemplates();
    }
  },
  { immediate: false }
);

watch(
  () => props.toEmails,
  (newEmails) => {
    if (newEmails && newEmails.length > 0) {
      toEmails.value = [...newEmails.filter((e) => e).map((e) => e.trim())];
    }
  },
  { immediate: true }
);

watch(
  () => props.toEmail,
  (newEmail) => {
    if (newEmail && !toEmails.value.includes(newEmail.trim())) {
      toEmails.value = [...toEmails.value, newEmail.trim()];
    }
  },
  { immediate: true }
);

const onTemplateSelected = (selected) => {
  selectedTemplate.value = selected;
  // For frontend templates, set content and emit event
  if (selected.value === "frontend-template" && props.frontendTemplate) {
    body.value = props.frontendTemplate.content || "";
    emit("onFrontendTemplateSelected", props.frontendTemplate);
    return;
  }
  const template = templates.value.find((t) => t.id === selected.value);
  body.value = template?.editableContent ?? "";
};

watch(
  () => props.frontendTemplate,
  (newTemplate) => {
    if (newTemplate) {
      // Add or update frontend template in the list
      const existingIndex = templateItems.value.findIndex((item) => item.value === "frontend-template");
      const frontendTemplateItem = {
        title: newTemplate.name,
        value: "frontend-template",
      };

      if (existingIndex >= 0) {
        templateItems.value[existingIndex] = frontendTemplateItem;
      } else {
        templateItems.value.unshift(frontendTemplateItem);
      }

      // Auto-select frontend template when it's provided
      selectedTemplate.value = frontendTemplateItem;
      onTemplateSelected(frontendTemplateItem);
    } else {
      // Remove frontend template from list if it's no longer provided
      const existingIndex = templateItems.value.findIndex((item) => item.value === "frontend-template");
      if (existingIndex >= 0) {
        templateItems.value.splice(existingIndex, 1);
        // If it was selected, clear selection
        if (selectedTemplate.value?.value === "frontend-template") {
          selectedTemplate.value = templateItems.value.length > 0 ? templateItems.value[0] : null;
          body.value = "";
        }
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.preselectedAttachment,
  (newValue) => {
    if (newValue) {
      selectedAttachment.value = newValue;
    }
  },
  { immediate: true }
);

const fetchTemplates = async () => {
  try {
    loading.value = true;
    errorMsg.value = "";
    const response = await templateService.getTemplates({
      templateTypes: props.types,
      intakeOrderId: props.intakeOrder?.id,
      orderInfoKey: props.orderInfo?.orderKey,
      intakeDocumentKey: props.intakeDocument?.documentKey,
    });
    templates.value = response.results.map((t) => new ClientTemplate(t));
    // Preserve frontend template if it exists
    const frontendTemplateItem = templateItems.value.find((item) => item.value === "frontend-template");

    // Add backend templates
    const backendTemplateItems = templates.value.map((t) => ({
      title: t.name ?? t.templateType,
      value: t.id,
    }));

    // Combine: frontend template first (if exists), then backend templates
    templateItems.value = frontendTemplateItem ? [frontendTemplateItem, ...backendTemplateItems] : backendTemplateItems;

    // Select frontend template if it exists, otherwise select first backend template
    if (frontendTemplateItem) {
      selectedTemplate.value = frontendTemplateItem;
      onTemplateSelected(frontendTemplateItem);
    } else if (templateItems.value.length > 0) {
      selectedTemplate.value = templateItems.value[0];
      onTemplateSelected(selectedTemplate.value);
    }
  } catch (error) {
    errorMsg.value = `Error fetching templates: ${error.message}`;
  } finally {
    loading.value = false;
  }
};

const fileInputRef = ref(null);
const uploadedFiles = ref([]);
let fileIdCounter = 0;
const onAttachFile = () => {
  fileInputRef.value?.click();
};

const handleFileUpload = async (event) => {
  const file = event.target.files[0];
  // Reset input so the same file can be re-selected if needed
  event.target.value = "";
  if (!file) return;
  const validationError = validateFile(file);
  if (validationError) {
    errorMsg.value = validationError;
    return;
  }

  // Check for duplicate by filename and file size
  const isDuplicate = uploadedFiles.value.some((f) => f.name === file.name && f.size === file.size);
  if (isDuplicate) return;

  uploading.value = true;
  try {
    const uploadKey = uuid.v4() + "/" + file.name;
    const link = await uploadService.uploadFile(file, uploadKey);
    downloadLinks.value.push(link);
    uploadedFiles.value.push({
      id: ++fileIdCounter,
      name: file.name,
      link: link,
      size: file.size,
    });
  } catch {
    errorMsg.value = `Failed to upload file "${file.name}". Please try again.`;
  } finally {
    uploading.value = false;
  }
};

const removeUploadedFile = (fileId) => {
  const fileIndex = uploadedFiles.value.findIndex((f) => f.id === fileId);
  if (fileIndex === -1) return;
  const file = uploadedFiles.value[fileIndex];
  downloadLinks.value = downloadLinks.value.filter((link) => link !== file.link);
  uploadedFiles.value.splice(fileIndex, 1);
};

const availableSuggestedFiles = computed(() => {
  return props.suggestedFiles.filter((sf) => !uploadedFiles.value.some((uf) => uf.link === sf.link));
});

const onAttachSuggestedFile = (suggestedFile) => {
  if (uploadedFiles.value.some((f) => f.link === suggestedFile.link)) return;
  downloadLinks.value.push(suggestedFile.link);
  uploadedFiles.value.push({
    id: ++fileIdCounter,
    name: suggestedFile.name,
    link: suggestedFile.link,
    size: 0,
  });
};

const onAttachmentSelected = (selected) => {
  // Allow clearing selection (null or undefined)
  selectedAttachment.value = selected || null;
};

const getAttachmentSummary = () => {
  if (!selectedAttachment.value || supportingDocumentsUrls.value.length === 0) {
    return "";
  }
  const attachmentName = selectedAttachment.value.title || "Filled Form";
  const docCount = supportingDocumentsUrls.value.length;
  return `${attachmentName} + ${docCount} supporting document${docCount !== 1 ? "s" : ""}`;
};

const resetForm = () => {
  body.value = "";
  subject.value = props.defaultSubject || "";
  selectedAttachment.value = null;
  downloadLinks.value = [];
  uploadedFiles.value = [];
  errorMsg.value = "";
  toEmails.value = [];
  selectedTemplate.value = null;
  selectedEmailTemplate.value = null;
  uploading.value = false;
  includeSupportingDocuments.value = true;
  emailSearchText.value = "";
};

const onSendAnother = () => {
  resetForm();
  emailSent.value = false;
  sentToSummary.value = "";
  initializeEmails();
};

const onSend = async () => {
  try {
    errorMsg.value = "";
    loading.value = true;

    // Build attachment data if selected
    const attachmentData = selectedAttachment.value?.value
      ? {
          type: selectedAttachment.value.value.type,
          value: selectedAttachment.value.value.type === "response_fax" ? selectedAttachment.value.value.s3_url : null,
        }
      : null;
    const isFlattenedFormAttachment = attachmentData?.type === "flattened_form";

    if (isFlattenedFormAttachment) {
      try {
        await new Promise((resolve, reject) => {
          emit("onPreSend", resolve, reject);
        });
      } catch (saveError) {
        errorMsg.value = "Failed to save prior authorization form before sending email. Please try again.";
        loading.value = false;
        return;
      }
    }

    const fileLinks = [...downloadLinks.value];
    if (includeSupportingDocuments.value && supportingDocumentsUrls.value.length > 0) {
      fileLinks.push(...supportingDocumentsUrls.value);
    }

    // Filter out empty emails
    const validEmails = toEmails.value.filter((e) => e && isEmailValid(e));

    const requestData = {
      subject: subject.value,
      body: body.value,
      toEmails: validEmails,
      fileLinks: fileLinks,
    };
    if (selectedTemplate.value?.value && selectedTemplate.value.value !== "frontend-template") {
      requestData.templateId = selectedTemplate.value.value;
    }
    if (attachmentData) {
      requestData.attachment = attachmentData;
    }
    if (props.intakeOrder) {
      requestData.intakeOrderId = props.intakeOrder.id;
    }
    if (props.orderInfo) {
      requestData.orderInfoKey = props.orderInfo.orderKey;
    }
    if (props.intakeDocument) {
      requestData.intakeDocumentKey = props.intakeDocument.documentKey;
    }
    if (props.deferSend) {
      const isFrontendTemplate = selectedTemplate.value?.value === "frontend-template";
      const frontendTemplateCategory =
        isFrontendTemplate && props.frontendTemplate ? props.frontendTemplate.category : null;
      emit("onComplete", { sent: true, queuePayload: requestData, isFrontendTemplate, frontendTemplateCategory });
      return;
    }
    const response = await emailService.postSendOutgoingEmail(requestData);
    const isFrontendTemplate = selectedTemplate.value?.value === "frontend-template";
    const frontendTemplateCategory =
      isFrontendTemplate && props.frontendTemplate ? props.frontendTemplate.category : null;
    emit("onComplete", { sent: response, isFrontendTemplate, frontendTemplateCategory });
    // Show success state — cap displayed emails to avoid overflow
    const MAX_DISPLAY_EMAILS = 3;
    if (validEmails.length <= MAX_DISPLAY_EMAILS) {
      sentToSummary.value = validEmails.join(", ");
    } else {
      const displayed = validEmails.slice(0, MAX_DISPLAY_EMAILS).join(", ");
      const remaining = validEmails.length - MAX_DISPLAY_EMAILS;
      sentToSummary.value = `${displayed} and ${remaining} other${remaining > 1 ? "s" : ""}`;
    }
    emailSent.value = true;
  } catch (error) {
    if (error instanceof Error && error.format) {
      errorMsg.value = error.format();
    } else {
      errorMsg.value = `Error sending email: ${error.message || "Unknown error"}`;
    }
  } finally {
    loading.value = false;
  }
};

defineExpose({});

const onCancel = () => {
  emit("onComplete", { sent: null });
};
</script>

<style lang="scss" scoped>
.email-editor {
  border: 1px solid $lh_dash_border;
  box-shadow: $lh_shadow_sm;
  border-radius: 16px !important;
}

.email-editor__title {
  font-family: $lh_font_family_dm_sans;
  font-size: 17px;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: $lh_color_navy_dark;
}
</style>
